import { useState, useEffect, useMemo } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import {
  getAutomationFlow, createAutomationFlow, updateAutomationFlow, setAutomationActions,
  getUnits, getSurveys, getBadges, getAllStudents, getUnitWorkshops
} from '@/lib/db'
import { supabase } from '@/lib/supabase'
import type { Unit, Survey, Badge, Student, UnitWorkshop, TriggerType, ActionType, FlowScopeType } from '@/types'
import { TRIGGER_META, ACTION_META, POPUP_ICONS, EMAIL_TEMPLATES } from './flowConstants'
import type { TriggerDepth } from './flowConstants'
import {
  Loader2, ChevronLeft, Save, Trash2,
  Zap, ArrowRight, AlertCircle, ChevronDown, ChevronUp
} from 'lucide-react'

interface ActionDraft {
  id: string
  action_type: ActionType
  action_config: Record<string, any>
}

interface RefData {
  units: Unit[]
  lessons: any[]
  contents: any[]
  exercises: any[]
  badges: Badge[]
  cohorts: any[]
  workshops: UnitWorkshop[]
  students: Student[]
  surveys: Survey[]
}

function uid() { return Math.random().toString(36).slice(2, 10) }

const DEPTH_ORDER: TriggerDepth[] = ['none', 'unit', 'lesson', 'content', 'exercise']
function depthIncludes(depth: TriggerDepth, level: TriggerDepth) {
  return DEPTH_ORDER.indexOf(depth) >= DEPTH_ORDER.indexOf(level)
}

export default function FlowEditor() {
  const { flowId } = useParams()
  const navigate = useNavigate()
  const isNew = !flowId || flowId === 'new'

  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')

  const [name, setName] = useState('')
  const [description, setDescription] = useState('')
  const [triggerType, setTriggerType] = useState<TriggerType>('student_completes_unit')
  const [triggerConfig, setTriggerConfig] = useState<Record<string, any>>({})
  const [scopeType, setScopeType] = useState<FlowScopeType>('all')
  const [scopeId, setScopeId] = useState('')
  const [isActive, setIsActive] = useState(true)
  const [actions, setActions] = useState<ActionDraft[]>([])

  const [ref, setRef] = useState<RefData>({
    units: [], lessons: [], contents: [], exercises: [],
    badges: [], cohorts: [], workshops: [], students: [], surveys: []
  })

  const [expandedAction, setExpandedAction] = useState<string | null>(null)

  useEffect(() => {
    async function load() {
      const [unitsRes, surveysRes, badgesRes, studentsRes, workshopsRes, lessonsRes, contentsRes, exercisesRes, cohortsRes] = await Promise.all([
        getUnits(),
        getSurveys(),
        getBadges(),
        getAllStudents(),
        getUnitWorkshops(),
        supabase.from('lessons').select('id, title, title_es, lesson_number, unit_id').eq('is_active', true).order('lesson_number'),
        supabase.from('contents').select('id, title, title_es, content_number, lesson_id').eq('is_active', true).order('content_number'),
        supabase.from('exercises').select('id, title, exercise_type, sort_order, content_id').eq('is_active', true).order('sort_order'),
        supabase.from('cohorts').select('id, name, is_active').eq('is_active', true).order('name'),
      ])

      setRef({
        units: unitsRes.data || [],
        lessons: lessonsRes.data || [],
        contents: contentsRes.data || [],
        exercises: exercisesRes.data || [],
        badges: badgesRes.data || [],
        cohorts: cohortsRes.data || [],
        workshops: workshopsRes.data || [],
        students: studentsRes.data || [],
        surveys: surveysRes.data || [],
      })

      if (!isNew) {
        const { data } = await getAutomationFlow(flowId!)
        if (data) {
          setName(data.name)
          setDescription(data.description || '')
          setTriggerType(data.trigger_type)
          setTriggerConfig(data.trigger_config || {})
          setScopeType(data.scope_type)
          setScopeId(data.scope_id || '')
          setIsActive(data.is_active)
          setActions((data.actions || []).sort((a, b) => a.sort_order - b.sort_order).map(a => ({
            id: uid(), action_type: a.action_type as ActionType, action_config: a.action_config
          })))
        }
      }
      setLoading(false)
    }
    load()
  }, [flowId, isNew])

  const triggerMeta = TRIGGER_META[triggerType]
  const depth = triggerMeta?.depth || 'none'

  const filteredLessons = useMemo(() => {
    if (!triggerConfig.unit_id) return ref.lessons
    return ref.lessons.filter((l: any) => l.unit_id === triggerConfig.unit_id)
  }, [ref.lessons, triggerConfig.unit_id])

  const filteredContents = useMemo(() => {
    if (triggerConfig.lesson_id) return ref.contents.filter((c: any) => c.lesson_id === triggerConfig.lesson_id)
    if (triggerConfig.unit_id) {
      const lessonIds = new Set(ref.lessons.filter((l: any) => l.unit_id === triggerConfig.unit_id).map((l: any) => l.id))
      return ref.contents.filter((c: any) => lessonIds.has(c.lesson_id))
    }
    return ref.contents
  }, [ref.contents, ref.lessons, triggerConfig.lesson_id, triggerConfig.unit_id])

  const filteredExercises = useMemo(() => {
    if (triggerConfig.content_id) return ref.exercises.filter((e: any) => e.content_id === triggerConfig.content_id)
    if (triggerConfig.lesson_id) {
      const contentIds = new Set(ref.contents.filter((c: any) => c.lesson_id === triggerConfig.lesson_id).map((c: any) => c.id))
      return ref.exercises.filter((e: any) => contentIds.has(e.content_id))
    }
    if (triggerConfig.unit_id) {
      const lessonIds = new Set(ref.lessons.filter((l: any) => l.unit_id === triggerConfig.unit_id).map((l: any) => l.id))
      const contentIds = new Set(ref.contents.filter((c: any) => lessonIds.has(c.lesson_id)).map((c: any) => c.id))
      return ref.exercises.filter((e: any) => contentIds.has(e.content_id))
    }
    return ref.exercises
  }, [ref.exercises, ref.contents, ref.lessons, triggerConfig.content_id, triggerConfig.lesson_id, triggerConfig.unit_id])

  function setTriggerField(key: string, value: string) {
    setTriggerConfig(prev => {
      const next = { ...prev, [key]: value }
      if (key === 'unit_id') { delete next.lesson_id; delete next.content_id; delete next.exercise_id }
      if (key === 'lesson_id') { delete next.content_id; delete next.exercise_id }
      if (key === 'content_id') { delete next.exercise_id }
      return next
    })
  }

  function addAction(type: ActionType) {
    const a: ActionDraft = { id: uid(), action_type: type, action_config: getDefaultActionConfig(type) }
    setActions(prev => [...prev, a])
    setExpandedAction(a.id)
  }
  function removeAction(id: string) { setActions(prev => prev.filter(a => a.id !== id)) }
  function updateAction(id: string, config: Record<string, any>) { setActions(prev => prev.map(a => a.id === id ? { ...a, action_config: config } : a)) }
  function moveAction(idx: number, dir: -1 | 1) {
    setActions(prev => {
      const arr = [...prev]; const ni = idx + dir
      if (ni < 0 || ni >= arr.length) return prev
      ;[arr[idx], arr[ni]] = [arr[ni], arr[idx]]; return arr
    })
  }

  async function handleSave() {
    if (!name.trim()) { setError('Ingresa un nombre'); return }
    if (actions.length === 0) { setError('Agrega al menos una acción'); return }
    setSaving(true); setError('')

    const flowData = {
      name: name.trim(),
      description: description.trim() || undefined,
      trigger_type: triggerType,
      trigger_config: triggerConfig,
      scope_type: scopeType,
      scope_id: scopeId || null,
      is_active: isActive,
    }
    const actionsData = actions.map((a, i) => ({ sort_order: i + 1, action_type: a.action_type, action_config: a.action_config }))

    if (isNew) {
      const { data, error: err } = await createAutomationFlow(flowData)
      if (err || !data) { setError((err as any)?.message || 'Error'); setSaving(false); return }
      await setAutomationActions(data.id, actionsData)
    } else {
      await updateAutomationFlow(flowId!, { ...flowData, description: flowData.description || null } as any)
      await setAutomationActions(flowId!, actionsData)
    }
    setSaving(false)
    navigate('/admin/flows')
  }

  const sc = "w-full px-3 py-2 bg-gray-100 border border-gray-300 rounded-lg text-gray-900"

  function unitLabel(u: Unit) { return (u as any).name_es || u.title || `Unidad ${(u as any).unit_number}` }
  function lessonLabel(l: any) {
    const u = ref.units.find(u => u.id === l.unit_id)
    return `${u ? unitLabel(u) + ' > ' : ''}Lección ${l.lesson_number}: ${l.title_es || l.title || ''}`
  }
  function contentLabel(c: any) {
    const l = ref.lessons.find((l: any) => l.id === c.lesson_id)
    return `${l ? 'L' + l.lesson_number + ' > ' : ''}Contenido ${c.content_number}: ${c.title_es || c.title || ''}`
  }
  function exerciseLabel(e: any) { return `${e.title || 'Ejercicio'} (${e.exercise_type})` }
  function studentLabel(s: Student) { const p = s.profile as any; return p ? `${p.first_name} ${p.last_name} (${p.email})` : s.id }

  if (loading) return <div className="flex items-center justify-center min-h-[400px]"><Loader2 className="w-8 h-8 animate-spin text-amber-500" /></div>

  return (
    <div className="max-w-4xl mx-auto space-y-6">
      {/* Header */}
      <div>
        <button onClick={() => navigate('/admin/flows')} className="text-sm text-gray-500 hover:text-gray-900 mb-3 flex items-center gap-1">
          <ChevronLeft className="w-4 h-4" /> Volver a flujos
        </button>
        <h1 className="text-2xl font-bold flex items-center gap-2">
          <Zap className="w-7 h-7 text-amber-400" />
          {isNew ? 'Crear flujo' : 'Editar flujo'}
        </h1>
      </div>

      {/* General */}
      <div className="bg-white rounded-xl border border-gray-200 p-5 space-y-4">
        <h2 className="text-sm font-bold text-gray-600 uppercase tracking-wide">Información general</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label htmlFor="flow-name" className="block text-sm text-gray-500 mb-1">Nombre del flujo *</label>
            <input id="flow-name" value={name} onChange={e => setName(e.target.value)} placeholder="Ej: Bienvenida al completar Unidad 1" className={sc} />
          </div>
          <div>
            <span className="block text-sm text-gray-500 mb-1">Estado</span>
            <button onClick={() => setIsActive(!isActive)}
              className={`px-4 py-2 rounded-lg text-sm font-medium w-full ${isActive ? 'bg-green-100 text-green-700 border border-green-300' : 'bg-gray-100 text-gray-500 border border-gray-300'}`}>
              {isActive ? 'Activo' : 'Inactivo'}
            </button>
          </div>
        </div>
        <div>
          <label htmlFor="flow-description" className="block text-sm text-gray-500 mb-1">Descripción (opcional)</label>
          <textarea id="flow-description" value={description} onChange={e => setDescription(e.target.value)} rows={2} placeholder="Que hace este flujo..." className={`${sc} resize-none`} />
        </div>
      </div>

      {/* TRIGGER */}
      <div className="bg-white rounded-xl border border-amber-200 p-5 space-y-4">
        <h2 className="text-sm font-bold text-amber-400 uppercase tracking-wide flex items-center gap-2">
          <Zap className="w-4 h-4" /> Evento iniciador (Trigger)
        </h2>

        <div>
          <label htmlFor="trigger-type" className="block text-sm text-gray-500 mb-1">Cuando ocurra...</label>
          <select id="trigger-type" value={triggerType} onChange={e => { setTriggerType(e.target.value as TriggerType); setTriggerConfig({}) }} className={sc}>
            {Object.entries(TRIGGER_META).map(([key, meta]) => (
              <option key={key} value={key}>{meta.icon} {meta.label}</option>
            ))}
          </select>
          {triggerMeta && <p className="text-xs text-gray-500 mt-1">{triggerMeta.description}</p>}
        </div>

        {depthIncludes(depth, 'unit') && (
          <div>
            <label htmlFor="trigger-unit" className="block text-sm text-gray-500 mb-1">Unidad</label>
            <select id="trigger-unit" value={triggerConfig.unit_id || ''} onChange={e => setTriggerField('unit_id', e.target.value)} className={sc}>
              <option value="">-- Cualquier unidad --</option>
              {ref.units.map(u => <option key={u.id} value={u.id}>{unitLabel(u)}</option>)}
            </select>
          </div>
        )}

        {depthIncludes(depth, 'lesson') && (
          <div>
            <label htmlFor="trigger-lesson" className="block text-sm text-gray-500 mb-1">
              Lección
              {triggerConfig.unit_id && <span className="text-gray-600 ml-1">({filteredLessons.length} disponibles)</span>}
            </label>
            <select id="trigger-lesson" value={triggerConfig.lesson_id || ''} onChange={e => setTriggerField('lesson_id', e.target.value)} className={sc}>
              <option value="">{depth === 'lesson' ? '-- Seleccionar lección --' : '-- Cualquier lección --'}</option>
              {filteredLessons.map((l: any) => <option key={l.id} value={l.id}>{lessonLabel(l)}</option>)}
            </select>
            {depthIncludes(depth, 'lesson') && !depthIncludes(depth, 'content') && !triggerConfig.unit_id && (
              <p className="text-xs text-amber-400/70 mt-1">Tip: selecciona una unidad primero para filtrar las lecciones</p>
            )}
          </div>
        )}

        {depthIncludes(depth, 'content') && (
          <div>
            <label htmlFor="trigger-content" className="block text-sm text-gray-500 mb-1">
              Contenido
              {(triggerConfig.unit_id || triggerConfig.lesson_id) && <span className="text-gray-600 ml-1">({filteredContents.length} disponibles)</span>}
            </label>
            <select id="trigger-content" value={triggerConfig.content_id || ''} onChange={e => setTriggerField('content_id', e.target.value)} className={sc}>
              <option value="">{depth === 'content' ? '-- Seleccionar contenido --' : '-- Cualquier contenido --'}</option>
              {filteredContents.map((c: any) => <option key={c.id} value={c.id}>{contentLabel(c)}</option>)}
            </select>
            {!triggerConfig.lesson_id && !triggerConfig.unit_id && (
              <p className="text-xs text-amber-400/70 mt-1">Tip: selecciona unidad y lección para filtrar</p>
            )}
          </div>
        )}

        {depthIncludes(depth, 'exercise') && (
          <div>
            <label htmlFor="trigger-exercise" className="block text-sm text-gray-500 mb-1">
              Ejercicio
              {(triggerConfig.unit_id || triggerConfig.lesson_id || triggerConfig.content_id) && <span className="text-gray-600 ml-1">({filteredExercises.length} disponibles)</span>}
            </label>
            <select id="trigger-exercise" value={triggerConfig.exercise_id || ''} onChange={e => setTriggerField('exercise_id', e.target.value)} className={sc}>
              <option value="">-- Seleccionar ejercicio --</option>
              {filteredExercises.map((e: any) => <option key={e.id} value={e.id}>{exerciseLabel(e)}</option>)}
            </select>
            {!triggerConfig.content_id && !triggerConfig.lesson_id && !triggerConfig.unit_id && (
              <p className="text-xs text-amber-400/70 mt-1">Tip: selecciona unidad, lección y contenido para filtrar</p>
            )}
          </div>
        )}

        {triggerMeta?.extraFields?.map(field => (
          <div key={field.key}>
            <label htmlFor={`trigger-${field.key}`} className="block text-sm text-gray-500 mb-1">{field.label}</label>
            {field.type === 'badge' && (
              <select id={`trigger-${field.key}`} value={triggerConfig[field.key] || ''} onChange={e => setTriggerConfig(prev => ({ ...prev, [field.key]: e.target.value }))} className={sc}>
                <option value="">-- Cualquier insignia --</option>
                {ref.badges.map(b => <option key={b.id} value={b.id}>{b.name} ({b.category})</option>)}
              </select>
            )}
            {field.type === 'cohort' && (
              <select id={`trigger-${field.key}`} value={triggerConfig[field.key] || ''} onChange={e => setTriggerConfig(prev => ({ ...prev, [field.key]: e.target.value }))} className={sc}>
                <option value="">-- Cualquier cohorte --</option>
                {ref.cohorts.map((c: any) => <option key={c.id} value={c.id}>{c.name}</option>)}
              </select>
            )}
            {field.type === 'workshop' && (
              <select id={`trigger-${field.key}`} value={triggerConfig[field.key] || ''} onChange={e => setTriggerConfig(prev => ({ ...prev, [field.key]: e.target.value }))} className={sc}>
                <option value="">-- Cualquier taller --</option>
                {ref.workshops.map(w => <option key={w.id} value={w.id}>{w.title} ({w.scheduled_date ? new Date(w.scheduled_date).toLocaleDateString('es-CL') : 'sin fecha'})</option>)}
              </select>
            )}
            {field.type === 'number' && (
              <input id={`trigger-${field.key}`} type="number" value={triggerConfig[field.key] || ''} onChange={e => setTriggerConfig(prev => ({ ...prev, [field.key]: Number(e.target.value) }))}
                placeholder="Ej: 7" className={sc} />
            )}
          </div>
        ))}

        {/* Scope */}
        <div className="border-t border-gray-200 pt-4">
          <span className="block text-sm text-gray-500 mb-1">Aplica a...</span>
          <div className="flex gap-2">
            {(['all', 'cohort', 'student'] as FlowScopeType[]).map(s => (
              <button key={s} onClick={() => { setScopeType(s); setScopeId('') }}
                className={`px-4 py-2 rounded-lg text-sm font-medium ${scopeType === s ? 'bg-amber-100 text-amber-700 border border-amber-300' : 'bg-gray-100 text-gray-500 border border-gray-300'}`}>
                {s === 'all' ? 'Todos los alumnos' : s === 'cohort' ? 'Una cohorte' : 'Un alumno'}
              </button>
            ))}
          </div>
          {scopeType === 'cohort' && (
            <>
              <label htmlFor="scope-cohort" className="sr-only">Seleccionar cohorte</label>
              <select id="scope-cohort" value={scopeId} onChange={e => setScopeId(e.target.value)} className={`${sc} mt-2`}>
                <option value="">Seleccionar cohorte...</option>
                {ref.cohorts.map((c: any) => <option key={c.id} value={c.id}>{c.name}</option>)}
              </select>
            </>
          )}
          {scopeType === 'student' && (
            <>
              <label htmlFor="scope-student" className="sr-only">Seleccionar alumno</label>
              <select id="scope-student" value={scopeId} onChange={e => setScopeId(e.target.value)} className={`${sc} mt-2`}>
                <option value="">Seleccionar alumno...</option>
                {ref.students.map(s => <option key={(s as any).profile_id || s.id} value={(s as any).profile_id || s.id}>{studentLabel(s)}</option>)}
              </select>
            </>
          )}
        </div>
      </div>

      <div className="flex justify-center"><ArrowRight className="w-8 h-8 text-gray-600 rotate-90" /></div>

      {/* ACTIONS */}
      <div className="bg-white rounded-xl border border-[#0055A4]/20 p-5 space-y-4">
        <h2 className="text-sm font-bold text-blue-400 uppercase tracking-wide flex items-center gap-2">
          <ArrowRight className="w-4 h-4" /> Acciones a ejecutar ({actions.length})
        </h2>

        {actions.map((action, idx) => {
          const meta = ACTION_META[action.action_type]
          const isExp = expandedAction === action.id
          return (
            <div key={action.id} className={`border rounded-xl transition-all ${isExp ? 'border-[#0055A4]/70' : 'border-gray-200'}`}>
              <div
                className="flex items-center gap-3 px-4 py-3 cursor-pointer"
                role="button"
                tabIndex={0}
                onClick={() => setExpandedAction(isExp ? null : action.id)}
                onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') setExpandedAction(isExp ? null : action.id) }}
              >
                <div className="flex flex-col gap-0.5">
                  <button onClick={e => { e.stopPropagation(); moveAction(idx, -1) }} disabled={idx === 0} className="text-gray-500 hover:text-gray-900 disabled:opacity-20"><ChevronUp className="w-3 h-3" /></button>
                  <button onClick={e => { e.stopPropagation(); moveAction(idx, 1) }} disabled={idx === actions.length - 1} className="text-gray-500 hover:text-gray-900 disabled:opacity-20"><ChevronDown className="w-3 h-3" /></button>
                </div>
                <span className="text-xs font-mono text-gray-500 w-5">{idx + 1}</span>
                <span className="text-lg">{meta?.icon}</span>
                <span className={`text-xs px-2 py-0.5 rounded-full border ${meta?.color || 'bg-gray-100 text-gray-500'}`}>{meta?.label}</span>
                <span className="flex-1 text-sm text-gray-500 truncate">{getActionSummary(action, ref)}</span>
                <button onClick={e => { e.stopPropagation(); removeAction(action.id) }} className="p-1 text-red-600 hover:text-red-300"><Trash2 className="w-4 h-4" /></button>
              </div>
              {isExp && (
                <div className="px-4 pb-4 border-t border-gray-200 pt-3">
                  <ActionConfigForm actionType={action.action_type} config={action.action_config} onChange={c => updateAction(action.id, c)} refData={ref} />
                </div>
              )}
            </div>
          )
        })}

        <div>
          <p className="text-xs text-gray-500 mb-2">Agregar acción:</p>
          <div className="flex flex-wrap gap-2">
            {Object.entries(ACTION_META).map(([key, meta]) => (
              <button key={key} onClick={() => addAction(key as ActionType)}
                className={`text-xs px-3 py-1.5 rounded-lg border flex items-center gap-1.5 transition-colors hover:opacity-80 ${meta.color}`}>
                <span>{meta.icon}</span> {meta.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg px-4 py-3 text-red-600 text-sm flex items-center gap-2">
          <AlertCircle className="w-4 h-4 flex-shrink-0" /> {error}
        </div>
      )}

      <div className="flex justify-end gap-3">
        <button onClick={() => navigate('/admin/flows')} className="px-4 py-2 bg-gray-100 text-gray-900 rounded-lg hover:bg-gray-200">Cancelar</button>
        <button onClick={handleSave} disabled={saving}
          className="px-6 py-2 bg-[#0055A4] text-white rounded-lg hover:bg-[#004080] disabled:opacity-50 flex items-center gap-2 font-medium">
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
          {saving ? 'Guardando...' : isNew ? 'Crear flujo' : 'Guardar cambios'}
        </button>
      </div>
    </div>
  )
}

// ============================================
// Certificate template selector
// ============================================
function CertificateTemplateSelector({ config, set, sc }: {
  config: Record<string, any>
  set: (k: string, v: any) => void
  sc: string
}) {
  const [templates, setTemplates] = useState<{ id: string; name: string }[]>([])

  useEffect(() => {
    supabase
      .from('certificate_templates')
      .select('id, name')
      .order('name')
      .then(({ data }) => { if (data) setTemplates(data) })
  }, [])

  return (
    <div className="space-y-3">
      <div>
        <label htmlFor="cert-template-id" className="block text-xs font-bold text-gray-500 mb-1">Plantilla de certificado</label>
        <select id="cert-template-id" value={config.template_id || ''} onChange={e => set('template_id', e.target.value)} className={sc}>
          <option value="">Seleccionar plantilla...</option>
          {templates.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
        </select>
        {templates.length === 0 && (
          <p className="text-xs text-amber-600 mt-1">No hay plantillas. Crea una en Certificados.</p>
        )}
      </div>
      <div>
        <label htmlFor="cert-achievement" className="block text-xs font-bold text-gray-500 mb-1">Texto del logro</label>
        <input
          id="cert-achievement"
          value={config.achievement_text || ''}
          onChange={e => set('achievement_text', e.target.value)}
          placeholder="Ej: Unidad 1 - Primeros Pasos"
          className={sc}
        />
        <p className="text-xs text-gray-500 mt-1">Este texto reemplaza el logro en el certificado</p>
      </div>
    </div>
  )
}

// ============================================
// Action config forms
// ============================================
function ActionConfigForm({ actionType, config, onChange, refData }: {
  actionType: ActionType; config: Record<string, any>; onChange: (c: Record<string, any>) => void; refData: RefData
}) {
  const set = (k: string, v: any) => onChange({ ...config, [k]: v })
  const sc = "w-full px-3 py-2 bg-gray-100 border border-gray-300 rounded-lg text-gray-900 text-sm"

  switch (actionType) {
    case 'send_email':
      return (
        <div className="space-y-3">
          <div>
            <label htmlFor="send_email-template" className="block text-xs font-bold text-gray-500 mb-1">Plantilla</label>
            <select id="send_email-template" value={config.template || 'custom'} onChange={e => set('template', e.target.value)} className={sc}>
              {EMAIL_TEMPLATES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
            </select>
          </div>
          <div>
            <label htmlFor="send_email-subject" className="block text-xs font-bold text-gray-500 mb-1">Asunto del correo</label>
            <input id="send_email-subject" value={config.subject || ''} onChange={e => set('subject', e.target.value)} placeholder="Felicidades por completar..." className={sc} />
          </div>
          <div>
            <label htmlFor="send_email-body" className="block text-xs font-bold text-gray-500 mb-1">Cuerpo del correo (HTML)</label>
            <textarea id="send_email-body" value={config.body_html || ''} onChange={e => set('body_html', e.target.value)} rows={5}
              placeholder="<h1>Felicidades!</h1><p>Has completado...</p>" className={`${sc} font-mono resize-none`} />
            <p className="text-xs text-gray-500 mt-1">Variables: {'{{student_name}}'}, {'{{student_email}}'}, {'{{trigger_detail}}'} | Remitente: cfi.nomadlexis@gmail.com</p>
          </div>
        </div>
      )
    case 'show_popup':
      return (
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label htmlFor="show_popup-title" className="block text-xs font-bold text-gray-500 mb-1">Título</label>
              <input id="show_popup-title" value={config.title || ''} onChange={e => set('title', e.target.value)} placeholder="Felicidades!" className={sc} />
            </div>
            <div>
              <label htmlFor="show_popup-type" className="block text-xs font-bold text-gray-500 mb-1">Tipo</label>
              <select id="show_popup-type" value={config.popup_type || 'message'} onChange={e => set('popup_type', e.target.value)} className={sc}>
                <option value="message">Mensaje</option><option value="celebration">Celebracion</option><option value="warning">Advertencia</option>
              </select>
            </div>
          </div>
          <div>
            <label htmlFor="show_popup-message" className="block text-xs font-bold text-gray-500 mb-1">Mensaje</label>
            <textarea id="show_popup-message" value={config.message || ''} onChange={e => set('message', e.target.value)} rows={2} placeholder="Has completado..." className={`${sc} resize-none`} />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label htmlFor="show_popup-icon" className="block text-xs font-bold text-gray-500 mb-1">Icono</label>
              <select id="show_popup-icon" value={config.icon || 'trophy'} onChange={e => set('icon', e.target.value)} className={sc}>
                {POPUP_ICONS.map(i => <option key={i.value} value={i.value}>{i.emoji} {i.label}</option>)}
              </select>
            </div>
            <div>
              <label htmlFor="show_popup-button" className="block text-xs font-bold text-gray-500 mb-1">Texto del botón</label>
              <input id="show_popup-button" value={config.button_text || 'Entendido'} onChange={e => set('button_text', e.target.value)} className={sc} />
            </div>
          </div>
        </div>
      )
    case 'send_survey':
      return (
        <div>
          <label htmlFor="send_survey-id" className="block text-xs font-bold text-gray-500 mb-1">Encuesta</label>
          <select id="send_survey-id" value={config.survey_id || ''} onChange={e => set('survey_id', e.target.value)} className={sc}>
            <option value="">Seleccionar encuesta...</option>
            {refData.surveys.map(s => <option key={s.id} value={s.id}>{s.title} ({s.questions.length} preguntas)</option>)}
          </select>
          {refData.surveys.length === 0 && <p className="text-xs text-yellow-700 mt-1">No hay encuestas. Crea una en la pestaña Encuestas.</p>}
        </div>
      )
    case 'award_points':
      return (
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label htmlFor="award_points-points" className="block text-xs font-bold text-gray-500 mb-1">Puntos</label>
            <input id="award_points-points" type="number" value={config.points || 0} onChange={e => set('points', Number(e.target.value))} className={sc} />
          </div>
          <div>
            <label htmlFor="award_points-reason" className="block text-xs font-bold text-gray-500 mb-1">Razón</label>
            <input id="award_points-reason" value={config.reason || ''} onChange={e => set('reason', e.target.value)} placeholder="Bonus por..." className={sc} />
          </div>
        </div>
      )
    case 'award_badge':
      return (
        <div>
          <label htmlFor="award_badge-id" className="block text-xs font-bold text-gray-500 mb-1">Insignia</label>
          <select id="award_badge-id" value={config.badge_id || ''} onChange={e => set('badge_id', e.target.value)} className={sc}>
            <option value="">Seleccionar insignia...</option>
            {refData.badges.map(b => <option key={b.id} value={b.id}>{b.name} ({b.category})</option>)}
          </select>
        </div>
      )
    case 'send_notification':
      return (
        <div className="space-y-3">
          <div>
            <label htmlFor="send_notification-title" className="block text-xs font-bold text-gray-500 mb-1">Título</label>
            <input id="send_notification-title" value={config.title || ''} onChange={e => set('title', e.target.value)} placeholder="Notificación" className={sc} />
          </div>
          <div>
            <label htmlFor="send_notification-message" className="block text-xs font-bold text-gray-500 mb-1">Mensaje</label>
            <textarea id="send_notification-message" value={config.message || ''} onChange={e => set('message', e.target.value)} rows={2} placeholder="Contenido..." className={`${sc} resize-none`} />
          </div>
        </div>
      )
    case 'send_whatsapp':
      return (
        <div>
          <label htmlFor="send_whatsapp-message" className="block text-xs font-bold text-gray-500 mb-1">Mensaje WhatsApp</label>
          <textarea id="send_whatsapp-message" value={config.message || ''} onChange={e => set('message', e.target.value)} rows={3} placeholder="Hola {{student_name}}!" className={`${sc} resize-none`} />
          <p className="text-xs text-yellow-700 mt-1">Requiere WhatsApp Business API (proximamente)</p>
        </div>
      )
    case 'generate_certificate':
      return <CertificateTemplateSelector config={config} set={set} sc={sc} />
    default:
      return <p className="text-gray-500 text-sm">Tipo no soportado</p>
  }
}

function getDefaultActionConfig(type: ActionType): Record<string, any> {
  const defaults: Record<string, any> = {
    send_email: { template: 'custom', subject: '', body_html: '' },
    show_popup: { title: '', message: '', popup_type: 'message', icon: 'trophy', button_text: 'Entendido' },
    send_survey: { survey_id: '' },
    award_points: { points: 50, reason: '' },
    award_badge: { badge_id: '' },
    send_notification: { title: '', message: '' },
    send_whatsapp: { message: '' },
    generate_certificate: { template_id: '', achievement_text: '' },
  }
  return defaults[type] || {}
}

function getActionSummary(a: ActionDraft, ref: RefData): string {
  const c = a.action_config
  switch (a.action_type) {
    case 'send_email': return c.subject || '(sin asunto)'
    case 'show_popup': return c.title || '(sin título)'
    case 'send_survey': return ref.surveys.find(s => s.id === c.survey_id)?.title || '(sin encuesta)'
    case 'award_points': return `${c.points || 0} puntos`
    case 'award_badge': return ref.badges.find(b => b.id === c.badge_id)?.name || '(sin insignia)'
    case 'send_notification': return c.title || '(sin título)'
    case 'send_whatsapp': return c.message ? 'Configurado' : '(sin mensaje)'
    case 'generate_certificate': return c.achievement_text || '(sin plantilla)'
    default: return ''
  }
}